export interface ModyoEntryMeta {
  uuid: string;
  name: string;
  slug: string;
  locale: string;
  published_at: string;
  updated_at: string;
}

export interface ModyoEntryResponse<T> {
  fields: T;
  meta?: ModyoEntryMeta;
}

export interface RespuestaErrorServicio {
  status: {
    code: string | number;
    dateTransaction?: string;
    desc?: string;
  };
  additionalStatus?: {
    code: number | string;
    desc: string;
  };
}

export type StatusServicio = RespuestaErrorServicio['status'];

export type AdditionalStatus = NonNullable<RespuestaErrorServicio['additionalStatus']>;

export interface CampoReferenciaModyo {
  uuid: string;
  content_type: string;
}

export interface CamposTituloModyo {
  peso: string;
  texto: string;
  cursiva: boolean | null;
  subrayar: boolean | null;
  'font-weight': string;
}

export interface CamposTextoEnriquecidoModyo {
  texto: string;
}

export interface EntradaReferenciadaModyo<T> {
  meta?: ModyoEntryMeta;
  fields: T;
}

export interface MensajeErrorServicioRawFields {
  codigo: string;
  codigo_http: number;
  titulo_mensaje: CampoReferenciaModyo | Record<string, never>;
  descripcion_mensaje: CampoReferenciaModyo | Record<string, never>;
  tipo_servicio: string[];
  mensaje_tecnico: string;
  nivel_severidad: string[];
  detiene_proceso: boolean;
  icono: string | Record<string, never>;
}

export type PesoTitulo = 'h1' | 'h2' | 'h3' | 'h4';

export interface MensajeErrorServicio {
  codigo: string;
  codigoHttp: string;
  tituloMensaje: string;
  pesoTitulo?: PesoTitulo;
  pesoFuenteTitulo?: string;
  descripcionMensaje: string;
  tipoServicio: string;
  mensajeTecnico: string;
  nivelSeveridad: 'exito' | 'advertencia' | 'error' | 'error_critico';
  detieneProceso: boolean;
  icono: 'success' | 'warning' | 'error' | 'info';
  meta?: ModyoEntryMeta;
}

export interface MensajeErrorServicioCollectionResponse {
  entries: Array<ModyoEntryResponse<MensajeErrorServicioRawFields>>;
  meta?: {
    total_entries: number;
    per_page: number;
    current_page: number;
    total_pages: number;
  };
}

export interface OpcionesBusquedaMensajeError {
  codigo: string;
  codigoHttp?: number | string;
  tipoServicio?: string;
}

export const PRIMERA_ENTRA_INDEX = 0;

export enum CodigoErrorBMM {
  errorBusGeneral = 506,

  excepcionPlataforma = 10001,
  errorEjecucionPrograma = 10002,
  sesionInvalida = 10011,
  canalNoDeclarado = 10021,
  canalDeshabilitado = 10022,
  servicioNoHabilitadoCanal = 10023,
  servicioNoDeclaradoCanal = 10024,
  servicioNoExiste = 10025,
  usuarioBantotalInvalido = 10026,
  usuarioSinAsignacionBantotal = 10027,
  usuarioNoHabilitadoServicio = 10028,
  usuarioExternoDeshabilitado = 10029,
  usuarioNoAsociadoServicioCanal = 10030,
  servicioMalConfigurado = 10031,

  clienteNoEncontrado = 30001,
  noExistenProductos = 30002,
  canalNoValido = 30003,
  aplicacionNoConfigurada = 30004,
  accionNoValida = 30005,
  tipoOperacionCuentaTradicional = 30006,
  valoresExentoGmf = 30007,
  campoTieneTarjetaObligatorio = 30008,
  valoresTieneTarjeta = 30009,

  aniOk = 0,
  aniCandidatoNoEncontrado = 1,
  aniCamposFormatoErroneo = 2,
  aniErrorInterno = 3001,
}

export const HTTP_STATUS_CODE_MAP: Record<number, string> = {
  200: 'TRANSACCION_EXITOSA',
  204: 'SIN_CONTENIDO',
  400: 'PETICION_INCORRECTA',
  401: 'NO_AUTORIZADO',
  403: 'PROHIBIDO',
  404: 'NO_ENCONTRADO',
  500: 'ERROR_SERVIDOR_INTERNO',
  502: 'BAD_GATEWAY',
  503: 'SERVICIO_NO_DISPONIBLE',
  504: 'GATEWAY_TIMEOUT',
  506: 'ERROR_BUS_INTEGRACION',
};

export enum EstadoDocumentoANI {
  vigente0 = '0',
  vigente1 = '1',
  bajaPerdidaSuspension = '12',
  bajaInterdiccionJudicial = '14',
  canceladaMuerte = '21',
  canceladaDobleCedulacion = '22',
  canceladaSuplantacion = '23',
  canceladaMenoriaEdad = '24',
  canceladaExtranjeria = '25',
  canceladaMalaElaboracion = '26',
  canceladaReasignacionSexo = '27',
  canceladaMuerteLey1365 = '51',
  canceladaIntentoDoblNoExpedida = '52',
  canceladaFalsaIdentidadNoExpedida = '53',
  canceladaMenoriaNoExpedida = '54',
  canceladaExtranjeriaNoExpedida = '55',
  canceladaMalaElaboracionNoExpedida = '56',
}

export function esDocumentoANIVigente(estado: string): boolean {
  return estado === EstadoDocumentoANI.vigente0 || estado === EstadoDocumentoANI.vigente1;
}

export function esErrorRecuperable(codigo: number | string): boolean {
  const codigoNum = typeof codigo === 'string' ? parseInt(codigo, 10) : codigo;
  const HTTP_503 = 503;
  const HTTP_504 = 504;

  const erroresRecuperables = [CodigoErrorBMM.sesionInvalida, HTTP_503, HTTP_504];

  return erroresRecuperables.includes(codigoNum);
}

export function esErrorCritico(codigo: number | string): boolean {
  const codigoNum = typeof codigo === 'string' ? parseInt(codigo, 10) : codigo;
  const HTTP_500 = 500;

  const erroresCriticos = [
    CodigoErrorBMM.excepcionPlataforma,
    CodigoErrorBMM.errorEjecucionPrograma,
    CodigoErrorBMM.servicioMalConfigurado,
    CodigoErrorBMM.errorBusGeneral,
  ];

  return erroresCriticos.includes(codigoNum) || codigoNum >= HTTP_500;
}
